import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Pagination,
  CircularProgress,
  Tooltip,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as StartIcon,
  CheckCircle as CompleteIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  PhotoCamera as PhotoIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import moment from 'moment';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import {
  useGetDriverTasksQuery,
  useGetDriverTaskStatsQuery,
  useCreateDriverTaskMutation,
  useCreateBulkDriverTasksMutation,
  useUpdateDriverTaskMutation,
  useStartDriverTaskMutation,
  useFinishDriverTaskMutation,
  useCancelDriverTaskMutation,
  useDeleteDriverTaskMutation
} from '../../redux/api/driverTasksApi';
import {
  DriverTask,
  DriverTaskType,
  DriverTaskStatus,
  DriverTaskPriority,
  DriverTaskScooter,
  GetDriverTasksParams
} from '../../resources/types/driverTasksTypes';
import { selectAuth } from '../../redux/slices/authSlice';

const getTaskTypeLabels = (t: TFunction): Record<DriverTaskType, string> => ({
  [DriverTaskType.PICKUP_BROKEN]: t('driverTasks.typePickupBroken'),
  [DriverTaskType.PICKUP_OFFLINE]: t('driverTasks.typePickupOffline'),
  [DriverTaskType.PICKUP_LOW_BATTERY]: t('driverTasks.typePickupLowBattery'),
  [DriverTaskType.PICKUP_BAD_PARKING]: t('driverTasks.typePickupBadParking'),
  [DriverTaskType.RELOCATE]: t('driverTasks.typeRelocate'),
  [DriverTaskType.PICKUP_IDLE]: t('driverTasks.typePickupIdle'),
});

const getStatusLabels = (t: TFunction): Record<DriverTaskStatus, string> => ({
  [DriverTaskStatus.PENDING]: t('driverTasks.statusPending'),
  [DriverTaskStatus.IN_PROGRESS]: t('driverTasks.statusInProgress'),
  [DriverTaskStatus.COMPLETED]: t('driverTasks.statusCompleted'),
  [DriverTaskStatus.CANCELED]: t('driverTasks.statusCanceled'),
});

const getPriorityLabels = (t: TFunction): Record<DriverTaskPriority, string> => ({
  [DriverTaskPriority.LOW]: t('driverTasks.priorityLow'),
  [DriverTaskPriority.MEDIUM]: t('driverTasks.priorityMedium'),
  [DriverTaskPriority.HIGH]: t('driverTasks.priorityHigh'),
  [DriverTaskPriority.URGENT]: t('driverTasks.priorityUrgent'),
});

const DriverTasksPage: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const auth = useSelector(selectAuth);
  const token = auth.info?.token;

  const TASK_TYPE_LABELS = getTaskTypeLabels(t);
  const STATUS_LABELS = getStatusLabels(t);
  const PRIORITY_LABELS = getPriorityLabels(t);

  const [filters, setFilters] = useState<GetDriverTasksParams>({
    page: 1,
    limit: 20
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<DriverTask | null>(null);
  const [finishDialogOpen, setFinishDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

  const [newTask, setNewTask] = useState({
    type: DriverTaskType.PICKUP_BROKEN,
    scooterId: '',
    scooterName: '',
    priority: DriverTaskPriority.MEDIUM,
    description: '',
    assignedTo: '',
    destination: { lat: 0, lon: 0, address: '' }
  });

  const [bulkTask, setBulkTask] = useState({
    type: DriverTaskType.PICKUP_BROKEN,
    scooterIds: [] as string[],
    scooterNames: '',
    priority: DriverTaskPriority.MEDIUM,
    description: '',
    assignedTo: '',
    destination: { lat: 0, lon: 0, address: '' }
  });

  const [finishData, setFinishData] = useState({ note: '', photos: [] as string[] });
  const [cancelReason, setCancelReason] = useState('');
  const [uploadedPhotos, setUploadedPhotos] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  const { data: tasksData, isLoading, refetch } = useGetDriverTasksQuery(filters);
  const { data: stats, refetch: refetchStats } = useGetDriverTaskStatsQuery();

  const [createTask] = useCreateDriverTaskMutation();
  const [createBulkTasks] = useCreateBulkDriverTasksMutation();
  const [updateTask] = useUpdateDriverTaskMutation();
  const [startTask] = useStartDriverTaskMutation();
  const [finishTask] = useFinishDriverTaskMutation();
  const [cancelTask] = useCancelDriverTaskMutation();
  const [deleteTask] = useDeleteDriverTaskMutation();

  const tasks = tasksData?.data || [];
  const pagination = tasksData?.pagination;

  const handleFilterChange = (key: keyof GetDriverTasksParams, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({ page: 1, limit: 20 });
  };

  const handlePageChange = (_: any, page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length || !token) return;
    setUploading(true);
    try {
      const files = Array.from(e.target.files);
      const uploadPromises = files.map(async file => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post('/api/files/persistent', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'x-access-token': token
          }
        });
        return response.data.path;
      });
      const paths = await Promise.all(uploadPromises);
      setUploadedPhotos(prev => [...prev, ...paths]);
    } catch (error) {
      console.error('Failed to upload photos:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await createTask({
        type: newTask.type,
        scooterId: newTask.scooterId,
        priority: newTask.priority,
        description: newTask.description || undefined,
        assignedTo: newTask.assignedTo || undefined,
        destination: newTask.type === DriverTaskType.RELOCATE ? newTask.destination : undefined
      }).unwrap();
      setCreateDialogOpen(false);
      setNewTask({
        type: DriverTaskType.PICKUP_BROKEN,
        scooterId: '',
        scooterName: '',
        priority: DriverTaskPriority.MEDIUM,
        description: '',
        assignedTo: '',
        destination: { lat: 0, lon: 0, address: '' }
      });
    } catch (error) {
      console.error('Failed to create task:', error);
    }
  };

  const handleBulkCreate = async () => {
    try {
      const scooterIds = bulkTask.scooterNames
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      
      if (scooterIds.length === 0) return;

      await createBulkTasks({
        type: bulkTask.type,
        scooterIds: scooterIds,
        priority: bulkTask.priority,
        description: bulkTask.description || undefined,
        assignedTo: bulkTask.assignedTo || undefined,
        destination: bulkTask.type === DriverTaskType.RELOCATE ? bulkTask.destination : undefined
      }).unwrap();
      
      setBulkDialogOpen(false);
      setBulkTask({
        type: DriverTaskType.PICKUP_BROKEN,
        scooterIds: [],
        scooterNames: '',
        priority: DriverTaskPriority.MEDIUM,
        description: '',
        assignedTo: '',
        destination: { lat: 0, lon: 0, address: '' }
      });
    } catch (error) {
      console.error('Failed to create bulk tasks:', error);
    }
  };

  const handleUpdate = async () => {
    if (!editingTask) return;
    try {
      const assignedToId = typeof editingTask.assignedTo === 'string' 
        ? editingTask.assignedTo 
        : editingTask.assignedTo?._id;
      
      await updateTask({
        id: editingTask._id,
        body: {
          priority: editingTask.priority,
          description: editingTask.description,
          destination: editingTask.destination,
          assignedTo: assignedToId || undefined
        }
      }).unwrap();
      setEditingTask(null);
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };

  const handleStart = async (id: string) => {
    try {
      await startTask(id).unwrap();
    } catch (error: any) {
      console.error('Failed to start task:', error);
      if (error?.status !== 500) {
        alert(t('driverTasks.errorStart'));
      }
    }
  };

  const handleFinish = async () => {
    if (!editingTask) return;
    try {
      await finishTask({
        id: editingTask._id,
        body: {
          photos: uploadedPhotos,
          note: finishData.note || undefined
        }
      }).unwrap();
      setFinishDialogOpen(false);
      setEditingTask(null);
      setFinishData({ note: '', photos: [] });
      setUploadedPhotos([]);
    } catch (error: any) {
      console.error('Failed to finish task:', error);
      if (error?.status === 500) {
        setFinishDialogOpen(false);
        setEditingTask(null);
        setFinishData({ note: '', photos: [] });
        setUploadedPhotos([]);
      }
    }
  };

  const handleCancel = async () => {
    if (!editingTask) return;
    try {
      await cancelTask({
        id: editingTask._id,
        body: { reason: cancelReason || undefined }
      }).unwrap();
      setCancelDialogOpen(false);
      setEditingTask(null);
      setCancelReason('');
    } catch (error: any) {
      console.error('Failed to cancel task:', error);
      if (error?.status === 500) {
        setCancelDialogOpen(false);
        setEditingTask(null);
        setCancelReason('');
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('driverTasks.confirmDelete'))) {
      try {
        await deleteTask(id).unwrap();
      } catch (error) {
        console.error('Failed to delete task:', error);
      }
    }
  };

  const getStatusColor = (status: DriverTaskStatus): 'default' | 'warning' | 'info' | 'success' | 'error' => {
    switch (status) {
      case DriverTaskStatus.PENDING: return 'warning';
      case DriverTaskStatus.IN_PROGRESS: return 'info';
      case DriverTaskStatus.COMPLETED: return 'success';
      case DriverTaskStatus.CANCELED: return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: DriverTaskPriority): 'default' | 'info' | 'warning' | 'error' => {
    switch (priority) {
      case DriverTaskPriority.LOW: return 'default';
      case DriverTaskPriority.MEDIUM: return 'info';
      case DriverTaskPriority.HIGH: return 'warning';
      case DriverTaskPriority.URGENT: return 'error';
      default: return 'default';
    }
  };

  const getScooterName = (task: DriverTask) => {
    if (typeof task.scooter === 'string') return task.scooter;
    return task.scooter?.name || '-';
  };

  const getScooterBattery = (task: DriverTask) => {
    if (typeof task.scooter === 'string') return null;
    return task.scooter?.battery;
  };

  const getAssigneeName = (task: DriverTask) => {
    if (!task.assignedTo) return '-';
    if (typeof task.assignedTo === 'string') return task.assignedToLogin || task.assignedTo;
    return `${task.assignedTo.firstName} ${task.assignedTo.lastName}`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{t('driverTasks.title')}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setBulkDialogOpen(true)}
          >
            {t('driverTasks.bulkCreate')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            {t('driverTasks.addTask')}
          </Button>
        </Box>
      </Box>

      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: theme.palette.warning.light }}>
              <Typography variant="h4">{stats.pending}</Typography>
              <Typography variant="body2">{t('driverTasks.statPending')}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: theme.palette.info.light }}>
              <Typography variant="h4">{stats.inProgress}</Typography>
              <Typography variant="body2">{t('driverTasks.statInProgress')}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: theme.palette.success.light }}>
              <Typography variant="h4">{stats.completedToday}</Typography>
              <Typography variant="body2">{t('driverTasks.statCompletedToday')}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: theme.palette.error.light }}>
              <Typography variant="h4">{stats.urgent}</Typography>
              <Typography variant="body2">{t('driverTasks.statUrgent')}</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('driverTasks.filterStatus')}</InputLabel>
            <Select
              value={filters.status || ''}
              label={t('driverTasks.filterStatus')}
              onChange={e => handleFilterChange('status', e.target.value || undefined)}
            >
              <MenuItem value="">{t('driverTasks.filterAll')}</MenuItem>
              {Object.entries(STATUS_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('driverTasks.filterType')}</InputLabel>
            <Select
              value={filters.type || ''}
              label={t('driverTasks.filterType')}
              onChange={e => handleFilterChange('type', e.target.value || undefined)}
            >
              <MenuItem value="">{t('driverTasks.filterAll')}</MenuItem>
              {Object.entries(TASK_TYPE_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('driverTasks.filterPriority')}</InputLabel>
            <Select
              value={filters.priority || ''}
              label={t('driverTasks.filterPriority')}
              onChange={e => handleFilterChange('priority', e.target.value || undefined)}
            >
              <MenuItem value="">{t('driverTasks.filterAll')}</MenuItem>
              {Object.entries(PRIORITY_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label={t('driverTasks.filterScooter')}
            value={filters.scooterName || ''}
            onChange={e => handleFilterChange('scooterName', e.target.value || undefined)}
            sx={{ width: 120 }}
          />

          <TextField
            size="small"
            label={t('driverTasks.filterDateFrom')}
            type="date"
            value={filters.dateFrom ? moment(filters.dateFrom).format('YYYY-MM-DD') : ''}
            onChange={e => handleFilterChange('dateFrom', e.target.value ? new Date(e.target.value).toISOString() : undefined)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            size="small"
            label={t('driverTasks.filterDateTo')}
            type="date"
            value={filters.dateTo ? moment(filters.dateTo).format('YYYY-MM-DD') : ''}
            onChange={e => handleFilterChange('dateTo', e.target.value ? new Date(e.target.value).toISOString() : undefined)}
            InputLabelProps={{ shrink: true }}
          />

          <IconButton onClick={handleClearFilters} size="small">
            <ClearIcon />
          </IconButton>
          <IconButton onClick={() => { refetch(); refetchStats(); }} size="small">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('driverTasks.colScooter')}</TableCell>
              <TableCell>{t('driverTasks.colType')}</TableCell>
              <TableCell>{t('driverTasks.colPriority')}</TableCell>
              <TableCell>{t('driverTasks.colStatus')}</TableCell>
              <TableCell>{t('driverTasks.colAssignee')}</TableCell>
              <TableCell>{t('driverTasks.colDescription')}</TableCell>
              <TableCell>{t('driverTasks.colCreated')}</TableCell>
              <TableCell align="right">{t('driverTasks.colActions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : tasks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {t('driverTasks.emptyState')}
                </TableCell>
              </TableRow>
            ) : (
              tasks.map(task => (
                <TableRow
                  key={task._id}
                  sx={{ '&:hover': { bgcolor: theme.palette.action.hover } }}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {getScooterName(task)}
                      </Typography>
                      {getScooterBattery(task) !== null && (
                        <Typography variant="caption" color="text.secondary">
                          {getScooterBattery(task)}%
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={TASK_TYPE_LABELS[task.type]}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={PRIORITY_LABELS[task.priority]}
                      size="small"
                      color={getPriorityColor(task.priority)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={STATUS_LABELS[task.status]}
                      size="small"
                      color={getStatusColor(task.status)}
                    />
                  </TableCell>
                  <TableCell>{getAssigneeName(task)}</TableCell>
                  <TableCell>
                    <Tooltip title={task.description || ''}>
                      <Typography noWrap sx={{ maxWidth: 150 }}>
                        {task.description || '-'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {moment(task.createdAt).format('DD.MM.YYYY HH:mm')}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      {task.status === DriverTaskStatus.PENDING && (
                        <Tooltip title={t('driverTasks.tooltipStart')}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleStart(task._id)}
                          >
                            <StartIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {task.status === DriverTaskStatus.IN_PROGRESS && (
                        <Tooltip title={t('driverTasks.tooltipFinish')}>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => {
                              setEditingTask(task);
                              setFinishDialogOpen(true);
                            }}
                          >
                            <CompleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {(task.status === DriverTaskStatus.PENDING || task.status === DriverTaskStatus.IN_PROGRESS) && (
                        <>
                          <Tooltip title={t('driverTasks.tooltipCancel')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setEditingTask(task);
                                setCancelDialogOpen(true);
                              }}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('driverTasks.tooltipEdit')}>
                            <IconButton
                              size="small"
                              onClick={() => setEditingTask(task)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title={t('driverTasks.tooltipDelete')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(task._id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination && pagination.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pagination.totalPages}
            page={filters.page || 1}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('driverTasks.createTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t('driverTasks.taskTypeLabel')}</InputLabel>
              <Select
                value={newTask.type}
                label={t('driverTasks.taskTypeLabel')}
                onChange={e => setNewTask(prev => ({ ...prev, type: e.target.value as DriverTaskType }))}
              >
                {Object.entries(TASK_TYPE_LABELS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={t('driverTasks.scooterIdLabel')}
              value={newTask.scooterId}
              onChange={e => setNewTask(prev => ({ ...prev, scooterId: e.target.value }))}
              fullWidth
              required
            />

            <FormControl fullWidth>
              <InputLabel>{t('driverTasks.priorityLabel')}</InputLabel>
              <Select
                value={newTask.priority}
                label={t('driverTasks.priorityLabel')}
                onChange={e => setNewTask(prev => ({ ...prev, priority: e.target.value as DriverTaskPriority }))}
              >
                {Object.entries(PRIORITY_LABELS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={t('driverTasks.descriptionLabel')}
              value={newTask.description}
              onChange={e => setNewTask(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />

            {newTask.type === DriverTaskType.RELOCATE && (
              <>
                <TextField
                  label={t('driverTasks.destLatLabel')}
                  type="number"
                  value={newTask.destination.lat}
                  onChange={e => setNewTask(prev => ({
                    ...prev,
                    destination: { ...prev.destination, lat: parseFloat(e.target.value) || 0 }
                  }))}
                  fullWidth
                />
                <TextField
                  label={t('driverTasks.destLonLabel')}
                  type="number"
                  value={newTask.destination.lon}
                  onChange={e => setNewTask(prev => ({
                    ...prev,
                    destination: { ...prev.destination, lon: parseFloat(e.target.value) || 0 }
                  }))}
                  fullWidth
                />
                <TextField
                  label={t('driverTasks.destAddressLabel')}
                  value={newTask.destination.address}
                  onChange={e => setNewTask(prev => ({
                    ...prev,
                    destination: { ...prev.destination, address: e.target.value }
                  }))}
                  fullWidth
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>{t('driverTasks.cancelBtn')}</Button>
          <Button onClick={handleCreate} variant="contained" disabled={!newTask.scooterId}>
            {t('driverTasks.createBtn')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('driverTasks.bulkTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t('driverTasks.taskTypeLabel')}</InputLabel>
              <Select
                value={bulkTask.type}
                label={t('driverTasks.taskTypeLabel')}
                onChange={e => setBulkTask(prev => ({ ...prev, type: e.target.value as DriverTaskType }))}
              >
                {Object.entries(TASK_TYPE_LABELS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={t('driverTasks.bulkScooterIdsLabel')}
              value={bulkTask.scooterNames}
              onChange={e => setBulkTask(prev => ({ ...prev, scooterNames: e.target.value }))}
              fullWidth
              multiline
              rows={3}
              helperText={t('driverTasks.bulkScooterIdsHelper')}
              required
            />

            <FormControl fullWidth>
              <InputLabel>{t('driverTasks.priorityLabel')}</InputLabel>
              <Select
                value={bulkTask.priority}
                label={t('driverTasks.priorityLabel')}
                onChange={e => setBulkTask(prev => ({ ...prev, priority: e.target.value as DriverTaskPriority }))}
              >
                {Object.entries(PRIORITY_LABELS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={t('driverTasks.descriptionLabel')}
              value={bulkTask.description}
              onChange={e => setBulkTask(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />

            {bulkTask.type === DriverTaskType.RELOCATE && (
              <>
                <TextField
                  label={t('driverTasks.destLatLabel')}
                  type="number"
                  value={bulkTask.destination.lat}
                  onChange={e => setBulkTask(prev => ({
                    ...prev,
                    destination: { ...prev.destination, lat: parseFloat(e.target.value) || 0 }
                  }))}
                  fullWidth
                />
                <TextField
                  label={t('driverTasks.destLonLabel')}
                  type="number"
                  value={bulkTask.destination.lon}
                  onChange={e => setBulkTask(prev => ({
                    ...prev,
                    destination: { ...prev.destination, lon: parseFloat(e.target.value) || 0 }
                  }))}
                  fullWidth
                />
                <TextField
                  label={t('driverTasks.destAddressLabel')}
                  value={bulkTask.destination.address}
                  onChange={e => setBulkTask(prev => ({
                    ...prev,
                    destination: { ...prev.destination, address: e.target.value }
                  }))}
                  fullWidth
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)}>{t('driverTasks.cancelBtn')}</Button>
          <Button onClick={handleBulkCreate} variant="contained" disabled={!bulkTask.scooterNames.trim()}>
            {t('driverTasks.createBtn')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editingTask !== null && !finishDialogOpen && !cancelDialogOpen} onClose={() => setEditingTask(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('driverTasks.editTitle')}</DialogTitle>
        <DialogContent>
          {editingTask && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>{t('driverTasks.priorityLabel')}</InputLabel>
                <Select
                  value={editingTask.priority}
                  label={t('driverTasks.priorityLabel')}
                  onChange={e => setEditingTask(prev => prev ? { ...prev, priority: e.target.value as DriverTaskPriority } : null)}
                >
                  {Object.entries(PRIORITY_LABELS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label={t('driverTasks.descriptionLabel')}
                value={editingTask.description || ''}
                onChange={e => setEditingTask(prev => prev ? { ...prev, description: e.target.value } : null)}
                fullWidth
                multiline
                rows={3}
              />

              <TextField
                label={t('driverTasks.assigneeIdLabel')}
                value={typeof editingTask.assignedTo === 'string' ? editingTask.assignedTo : (editingTask.assignedTo?._id || '')}
                onChange={e => setEditingTask(prev => prev ? { ...prev, assignedTo: e.target.value || undefined } : null)}
                fullWidth
                helperText={t('driverTasks.assigneeIdHelper')}
              />

              {editingTask.type === DriverTaskType.RELOCATE && (
                <>
                  <TextField
                    label={t('driverTasks.destLatLabel')}
                    type="number"
                    value={editingTask.destination?.lat || 0}
                    onChange={e => setEditingTask(prev => prev ? {
                      ...prev,
                      destination: { ...prev.destination!, lat: parseFloat(e.target.value) || 0 }
                    } : null)}
                    fullWidth
                  />
                  <TextField
                    label={t('driverTasks.destLonLabel')}
                    type="number"
                    value={editingTask.destination?.lon || 0}
                    onChange={e => setEditingTask(prev => prev ? {
                      ...prev,
                      destination: { ...prev.destination!, lon: parseFloat(e.target.value) || 0 }
                    } : null)}
                    fullWidth
                  />
                  <TextField
                    label={t('driverTasks.destAddressLabel')}
                    value={editingTask.destination?.address || ''}
                    onChange={e => setEditingTask(prev => prev ? {
                      ...prev,
                      destination: { ...prev.destination!, address: e.target.value }
                    } : null)}
                    fullWidth
                  />
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingTask(null)}>{t('driverTasks.cancelBtn')}</Button>
          <Button onClick={handleUpdate} variant="contained">
            {t('driverTasks.saveBtn')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={finishDialogOpen} onClose={() => { setFinishDialogOpen(false); setEditingTask(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{t('driverTasks.finishTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label={t('driverTasks.noteLabel')}
              value={finishData.note}
              onChange={e => setFinishData(prev => ({ ...prev, note: e.target.value }))}
              fullWidth
              multiline
              rows={3}
            />

            <Box>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="photo-upload"
                multiple
                type="file"
                onChange={handlePhotoUpload}
              />
              <label htmlFor="photo-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={uploading ? <CircularProgress size={20} /> : <PhotoIcon />}
                  disabled={uploading}
                >
                  {t('driverTasks.addPhotoBtn')}
                </Button>
              </label>
            </Box>

            {uploadedPhotos.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {uploadedPhotos.map((photo, index) => (
                  <Box key={index} sx={{ position: 'relative' }}>
                    <img
                      src={`https://whee.pro/public/${photo}`}
                      alt={`Photo ${index + 1}`}
                      style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }}
                    />
                    <IconButton
                      size="small"
                      sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper' }}
                      onClick={() => setUploadedPhotos(prev => prev.filter((_, i) => i !== index))}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setFinishDialogOpen(false); setEditingTask(null); setUploadedPhotos([]); }}>
            {t('driverTasks.cancelBtn')}
          </Button>
          <Button onClick={handleFinish} variant="contained" color="success">
            {t('driverTasks.finishBtn')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialogOpen} onClose={() => { setCancelDialogOpen(false); setEditingTask(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{t('driverTasks.cancelTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            label={t('driverTasks.cancelReasonLabel')}
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCancelDialogOpen(false); setEditingTask(null); }}>
            {t('driverTasks.backBtn')}
          </Button>
          <Button onClick={handleCancel} variant="contained" color="error">
            {t('driverTasks.cancelTaskBtn')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DriverTasksPage;
